import * as authentication from '@cascade/react/authentication';
import { useEffect, useMemo } from 'react';
import {
  OIDC_PARAM,
  signInCallbackParams,
} from './Authentication.constants.ts';
import type { TAuthenticationGateProps } from './Authentication.types.ts';
import { useConsumedSearchParams } from '#hooks/use-consumed-search-params/index.ts';
import { useRequestedUrl } from '#hooks/use-requested-url/index.ts';
import { navigateExternal } from '#utils/navigate-external/index.ts';

const oidcParams = [OIDC_PARAM];

export const AuthenticationGate = ({
  loading,
  unauthenticated,
  transientParams,
  children,
}: TAuthenticationGateProps) => {
  const { reason, isAuthenticated, canStartSignIn, signInWithProvider } =
    authentication.useAuthentication();

  const allTransientParams = useMemo(
    () => [...signInCallbackParams, OIDC_PARAM, ...(transientParams ?? [])],
    [transientParams],
  );

  const {
    hasRequestedUrl,
    rememberRequestedUrl,
    forgetRequestedUrl,
    goToRequestedUrl,
  } = useRequestedUrl(allTransientParams);

  const oidcSearchParams = useConsumedSearchParams(oidcParams);
  const oidc = oidcSearchParams.get(OIDC_PARAM) ?? undefined;
  const isSignedOut = reason === 'signedOut';
  const isLanding = isAuthenticated && hasRequestedUrl();

  useEffect(() => {
    if (isSignedOut) {
      forgetRequestedUrl();

      return;
    }

    if (canStartSignIn) {
      rememberRequestedUrl();
    }
  }, [isSignedOut, canStartSignIn, rememberRequestedUrl, forgetRequestedUrl]);

  useEffect(() => {
    if (!oidc || !canStartSignIn) {
      return;
    }

    signInWithProvider(oidc)
      .then((result) => {
        if (result.kind === 'redirect') {
          navigateExternal(result.url);
        }
      })
      .catch(() => undefined);
  }, [oidc, canStartSignIn, signInWithProvider]);

  useEffect(() => {
    if (isLanding) {
      goToRequestedUrl();
    }
  }, [isLanding, goToRequestedUrl]);

  return (
    <authentication.AuthenticationGate
      loading={loading}
      unauthenticated={unauthenticated}
    >
      {isLanding ? loading : children}
    </authentication.AuthenticationGate>
  );
};
